import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import AdminToolbar from './AdminToolbar';
import Navigation from './Navigation';
import ContractAnalysisLanding from './ContractAnalysisLanding';
import LegalFoundations from './LegalFoundations';
import InteractiveLegalDiagram from './InteractiveLegalDiagram';
import SourceManager from './SourceManager';
import DimensionsManager from './DimensionsManager';
import LegalQA from './LegalQA';
import LawAnalysis from './LawAnalysis';
import PolicyAnalysis from './PolicyAnalysis';
import RegulationAnalysis from './RegulationAnalysis';
import LegalStrategy from './LegalStrategy';
import LandingPage from './LandingPage';
import LoginPage from './LoginPage';
import CommunicationsDashboard from './CommunicationsDashboard';
import PatentAnalysis from './PatentAnalysis';
import ComplianceAnalysis from './ComplianceAnalysis';
import LegalServices from './LegalServices';
import TaxStrategy from './TaxStrategy';
import ServiceLanding from './ServiceLanding';
import ContributionsPage from './ContributionsPage';
import ContributionWidget from './ContributionWidget';
import LegalDisclaimer from './LegalDisclaimer';
import {
  lawAnalysisConfig,
  policyAnalysisConfig,
  regulationAnalysisConfig,
  legalStrategyConfig,
  patentAnalysisConfig,
  complianceConfig,
  legalServicesConfig,
  taxStrategyConfig,
} from './serviceLandingConfigs';

// Component to protect routes that require authentication
function ProtectedRoute({ children }) {
  const { user, loading, login } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={login} />;
  }

  return children;
}

// UPL safeguard: high-risk features (legal strategy, business filing, tax strategy)
// are admin-only because they generate personalized advice that crosses the UPL line.
function AdminOnlyRoute({ children, serviceName }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="max-w-lg mx-auto mt-16 px-4">
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Service Restricted</h2>
          <p className="text-sm text-gray-600 mb-4">
            {serviceName || 'This service'} generates personalized legal analysis that requires professional oversight.
            It is only available to authorized administrators.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-4">
            <p className="text-xs text-amber-700">
              <strong>Why is this restricted?</strong> Under unauthorized practice of law (UPL) regulations,
              AI tools that generate personalized legal advice, fill in legal documents based on your specific facts,
              or recommend specific legal strategies may only be offered under professional supervision.
            </p>
          </div>
          <p className="text-sm text-gray-600">
            For personalized legal guidance, we recommend consulting a <strong>licensed attorney</strong> in your jurisdiction.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {user && <AdminToolbar />}
      <Navigation />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/qa" element={<LegalQA />} />
        <Route path="/qa/:id" element={<LegalQA />} />
        <Route path="/legal-qa" element={<LegalQA />} />
        {/* Service landing pages — show hero + features + login when not authenticated */}
        <Route path="/law-analysis" element={<ServiceLanding component={LawAnalysis} {...lawAnalysisConfig} />} />
        <Route path="/law-analysis/:id" element={<ServiceLanding component={LawAnalysis} {...lawAnalysisConfig} />} />
        <Route path="/policy-analysis" element={<ServiceLanding component={PolicyAnalysis} {...policyAnalysisConfig} />} />
        <Route path="/policy-analysis/:id" element={<ServiceLanding component={PolicyAnalysis} {...policyAnalysisConfig} />} />
        <Route path="/regulation-analysis" element={<ServiceLanding component={RegulationAnalysis} {...regulationAnalysisConfig} />} />
        <Route path="/regulation-analysis/:uuid" element={<ServiceLanding component={RegulationAnalysis} {...regulationAnalysisConfig} />} />
        <Route path="/legal-strategy" element={<AdminOnlyRoute serviceName="Legal Strategy"><ServiceLanding component={LegalStrategy} {...legalStrategyConfig} /></AdminOnlyRoute>} />
        <Route path="/legal-strategy/:id" element={<AdminOnlyRoute serviceName="Legal Strategy"><ServiceLanding component={LegalStrategy} {...legalStrategyConfig} /></AdminOnlyRoute>} />
        <Route path="/contract-analysis" element={<ContractAnalysisLanding />} />
        <Route path="/contract-analysis/:uuid" element={<ContractAnalysisLanding />} />
        <Route path="/tax-strategy" element={<AdminOnlyRoute serviceName="Tax Strategy"><ServiceLanding component={TaxStrategy} {...taxStrategyConfig} /></AdminOnlyRoute>} />
        <Route path="/tax-strategy/:id" element={<AdminOnlyRoute serviceName="Tax Strategy"><ServiceLanding component={TaxStrategy} {...taxStrategyConfig} /></AdminOnlyRoute>} />
        <Route path="/legal-services" element={<AdminOnlyRoute serviceName="Business Filing"><ServiceLanding component={LegalServices} {...legalServicesConfig} /></AdminOnlyRoute>} />
        <Route path="/legal-services/:uuid" element={<AdminOnlyRoute serviceName="Business Filing"><ServiceLanding component={LegalServices} {...legalServicesConfig} /></AdminOnlyRoute>} />
        <Route path="/legal-services/:uuid/copilot" element={<AdminOnlyRoute serviceName="Business Filing"><ServiceLanding component={LegalServices} {...legalServicesConfig} /></AdminOnlyRoute>} />
        <Route path="/patent-analysis" element={<ServiceLanding component={PatentAnalysis} {...patentAnalysisConfig} />} />
        <Route path="/compliance" element={<ServiceLanding component={ComplianceAnalysis} {...complianceConfig} />} />

        {/* Admin/internal protected routes */}
        <Route path="/source-manager" element={
          <ProtectedRoute>
            <SourceManager />
          </ProtectedRoute>
        } />
        <Route path="/dimensions-manager" element={
          <ProtectedRoute>
            <DimensionsManager />
          </ProtectedRoute>
        } />
        <Route path="/legal-foundations" element={
          <ProtectedRoute>
            <LegalFoundations />
          </ProtectedRoute>
        } />
        <Route path="/legal-diagram" element={
          <ProtectedRoute>
            <InteractiveLegalDiagram />
          </ProtectedRoute>
        } />
        <Route path="/admin/communications" element={
          <ProtectedRoute>
            <CommunicationsDashboard />
          </ProtectedRoute>
        } />
        <Route path="/contributions" element={
          <ProtectedRoute>
            <ContributionsPage />
          </ProtectedRoute>
        } />
      </Routes>
      <ContributionWidget />
      <LegalDisclaimer variant="footer" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
